import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { blue, red } from "@mui/material/colors";

import { useAppointmentOutcome } from "hooks";
import { merge } from "lodash";

import Dialog from "components/dialog";
import {
  APPOINTMENT_OUTCOME_REASON_EXCLUDED,
  APPOINTMENT_OUTCOME_REASON_TYPES
} from "constants/appointment-outcome-reasons";
import {
  APPOINTMENT_STATUS_CANCELED,
  APPOINTMENT_STATUS_NO_SHOW,
  APPOINTMENT_STATUS_UNCONFIRMED
} from "constants/appointments";
import {
  HEARING_LOSS_LEVEL,
  HEARING_LOSS_SHAPE,
  HEARING_LOSS_TYPE
} from "constants/audiology";
import {
  ALLOW_ENTERING_CANCELLATION_REASON,
  COMPANION_PRESENT_FIELD,
  HEARING_AID_DEMO_FIELD,
  CONFIGURABLE_APPOINTMENT_CHECKLIST,
  NEW_APPOINTMENT_OUTCOME_CHANGES,
  REMOVE_EXCLUDE_FROM_APPOINTMENT_OUTCOMES
} from "constants/feature-flags";
import { CONTACT_RELATIONSHIP } from "constants/patients";
import { DEFAULT_TEMPLATE_TYPES } from "constants/templates";
import PatientAssignedTags from "features/patients/patient-assigned-tags";
import AppointmentChecklist from "features/scheduling/scheduler/appointment-preview/appointment-preview-content/appointment-checklist.component";
import { getStatusTransition } from "features/scheduling/utils/status-transitions.util";
import { useGetAppointmentOutcomeReasonsQuery } from "services/appointment-outcome-reasons";
import { useGetAppointmentQuery } from "services/appointments";
import { ICD10Code } from "services/icd10-codes";
import { AppointmentOutcomeReason } from "services/types/appointment-outcome-reasons.type";

import CheckboxFormField from "../checkbox-form-field";
import CircularProgress from "../circular-progress";
import SelectBooleanValue from "../custom-filters-list/filters/select-boolean-value.component";
import ICDCodesFormField from "../icd10-codes-form-field";
import InputFormField from "../input-form-field";
import SearchableSelectFormField from "../searchable-select-form-field";
import SelectFormField from "../select-form-field";
import WordProcessorField from "../word-processor-field";

export interface AppointmentOutcomeForm {
  outcome: string;
  outcome_reason: string;
  outcome_notes: string;
  hearing_test_conducted: { option: string; value: boolean } | null;
  companion_present: boolean;
  hearing_aid_demo_performed: boolean;
  companion_type: number | null;
  companion_name: string;
  telehealth_provider: string;
  chart_note_id: number | null;
  diagnosis: {
    hearing_loss_left: number | null;
    hearing_loss_right: number | null;
    hearing_loss_type_left: number | null;
    hearing_loss_type_right: number | null;
    hearing_loss_shape_left: number | null;
    hearing_loss_shape_right: number | null;
    icd10_codes: ICD10Code[];
  };
}

const DEFAULT_FORM_VALUES = {
  outcome: "",
  outcome_reason: "",
  outcome_notes: "",
  hearing_test_conducted: null,
  companion_present: false,
  hearing_aid_demo_performed: false,
  companion_type: null,
  companion_name: "",
  telehealth_provider: "",
  chart_note_id: null,
  diagnosis: {
    hearing_loss_left: null,
    hearing_loss_right: null,
    hearing_loss_type_left: null,
    hearing_loss_type_right: null,
    hearing_loss_shape_left: null,
    hearing_loss_shape_right: null,
    icd10_codes: []
  }
};

export default function AppointmentOutcomeDialog() {
  const flags = useFlags();
  const removeExcludedOutcomeReason =
    flags[REMOVE_EXCLUDE_FROM_APPOINTMENT_OUTCOMES];
  const newAppointmentOutcomeChangesEnabled =
    flags[NEW_APPOINTMENT_OUTCOME_CHANGES];
  const allowEnteringCancellationReason =
    flags[ALLOW_ENTERING_CANCELLATION_REASON];
  const companionPresentFieldVisible = flags[COMPANION_PRESENT_FIELD];
  const hearingAidDemoFieldVisible = flags[HEARING_AID_DEMO_FIELD];
  const configurableChecklistEnabled = flags[CONFIGURABLE_APPOINTMENT_CHECKLIST];
  const {
    appointmentOutcomeDialogOpen,
    setAppointmentOutcomeDialogOpen,
    submitOutcomeReason,
    onCancelAction,
    isUpdatingOutcome,
    currentAppointmentId,
    appointmentStatus
  } = useAppointmentOutcome();

  const isCancellation =
    allowEnteringCancellationReason &&
    (appointmentStatus === APPOINTMENT_STATUS_CANCELED ||
      appointmentStatus === APPOINTMENT_STATUS_NO_SHOW);

  const [canAppointmentBeCompleted, setCanAppointmentBeCompleted] =
    useState(false);

  const { data: appointment, isLoading } = useGetAppointmentQuery(
    { id: Number(currentAppointmentId) },
    {
      skip: !currentAppointmentId,
      refetchOnMountOrArgChange: true
    }
  );

  const patientId = appointment?.patient.id;

  const statusTransition = getStatusTransition(
    appointment?.status || APPOINTMENT_STATUS_UNCONFIRMED
  );

  const diagnosisIsDisabled = Boolean(
    appointment?.diagnosis?.report?.is_signed
  );

  const outcomeOptions = useMemo(
    () =>
      APPOINTMENT_OUTCOME_REASON_TYPES.getOptions(
        removeExcludedOutcomeReason ? [APPOINTMENT_OUTCOME_REASON_EXCLUDED] : []
      ),
    [removeExcludedOutcomeReason]
  );

  const chartNoteText = useMemo(() => {
    return newAppointmentOutcomeChangesEnabled
      ? appointment?.chartNote?.text || ""
      : "";
  }, [newAppointmentOutcomeChangesEnabled, appointment]);

  const defaultValues: AppointmentOutcomeForm = useMemo(() => {
    return merge({}, DEFAULT_FORM_VALUES, {
      chart_note_id: appointment?.chartNote?.id || null,
      hearing_test_conducted: {
        value: Boolean(appointment?.hearing_test_conducted),
        label: appointment?.hearing_test_conducted ? "Yes" : "No"
      },
      companion_present:
        appointment?.companion_present || Boolean(appointment?.companionType),
      hearing_aid_demo_performed:
        appointment?.hearing_aid_demo_performed || false,
      companion_type: appointment?.companionType || null,
      companion_name: appointment?.companionName || "",
      diagnosis: {
        hearing_loss_left: appointment?.diagnosis?.hearing_loss_left || null,
        hearing_loss_right: appointment?.diagnosis?.hearing_loss_right || null,
        hearing_loss_type_left:
          appointment?.diagnosis?.hearing_loss_type_left || null,
        hearing_loss_type_right:
          appointment?.diagnosis?.hearing_loss_type_right || null,
        hearing_loss_shape_left:
          appointment?.diagnosis?.hearing_loss_shape_left || null,
        hearing_loss_shape_right:
          appointment?.diagnosis?.hearing_loss_shape_right || null,
        icd10_codes:
          appointment?.diagnosis?.icd10_codes ||
          appointment?.chartNote?.icd10_codes ||
          []
      },
      outcome:
        appointment?.outcome ||
        appointment?.hearing_test_conducted ||
        newAppointmentOutcomeChangesEnabled ||
        removeExcludedOutcomeReason ||
        isCancellation
          ? ""
          : String(APPOINTMENT_OUTCOME_REASON_EXCLUDED),
      outcome_reason: appointment?.outcome_reason || "",
      outcome_notes: appointment?.outcome_notes || chartNoteText
    });
  }, [
    removeExcludedOutcomeReason,
    appointment,
    chartNoteText,
    newAppointmentOutcomeChangesEnabled,
    isCancellation
  ]);

  const methods = useForm<AppointmentOutcomeForm>({
    defaultValues
  });

  const {
    control,
    watch,
    formState: { isValid },
    getValues,
    setValue,
    reset
  } = methods;

  const [selectedOutcome, isTestConducted, isCompanionPresent] = watch([
    "outcome",
    "hearing_test_conducted",
    "companion_present"
  ]);

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const { data: reasons = [] } = useGetAppointmentOutcomeReasonsQuery(
    selectedOutcome,
    {
      skip: !currentAppointmentId || !selectedOutcome
    }
  );

  const copyFromLeftToRight = useCallback(() => {
    setValue(
      "diagnosis.hearing_loss_right",
      getValues("diagnosis.hearing_loss_left"),
      {
        shouldDirty: true,
        shouldValidate: true
      }
    );
    setValue(
      "diagnosis.hearing_loss_type_right",
      getValues("diagnosis.hearing_loss_type_left"),
      {
        shouldDirty: true,
        shouldValidate: true
      }
    );
    setValue(
      "diagnosis.hearing_loss_shape_right",
      getValues("diagnosis.hearing_loss_shape_left"),
      {
        shouldDirty: true,
        shouldValidate: true
      }
    );
  }, [getValues, setValue]);

  const handleSubmit = () => {
    const formData = getValues();
    const diagnosisValues = formData.diagnosis;
    return submitOutcomeReason({
      ...formData,
      companion_type: formData.companion_present
        ? formData.companion_type || null
        : null,
      companion_name: formData.companion_present
        ? formData.companion_name || ""
        : "",
      hearing_test_conducted: formData.hearing_test_conducted?.value,
      diagnosis: {
        ...diagnosisValues,
        icd10_codes: formData.diagnosis.icd10_codes.map(({ id }) => id),
        patient: appointment?.patient?.id
      },
      status: canAppointmentBeCompleted ? statusTransition : undefined
    });
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <FormProvider {...methods}>
      <form>
        <Dialog
          title="Appointment outcome"
          onClose={() => {
            if (isUpdatingOutcome) return;
            setAppointmentOutcomeDialogOpen(false);
            if (onCancelAction) {
              onCancelAction();
            }
          }}
          open={appointmentOutcomeDialogOpen}
          minWidth={768}
          maxWidth="lg"
          dialogContentProps={{
            sx: {
              padding: "20px 40px !important",
              minWidth: "768px",
              maxWidth: "1024px"
            }
          }}
        >
          <Stack width="100%" direction="column" spacing={1} sx={{ mb: 4 }}>
            {!isCancellation && (
              <SelectBooleanValue
                name="hearing_test_conducted"
                label="Was a hearing test conducted?"
                control={control}
                onOptionSelected={(option) => {
                  if (option?.value) {
                    setValue("outcome", "");
                  }
                }}
                required
              />
            )}

            {!isCancellation && isTestConducted?.value && (
              <>
                <Typography
                  variant="subtitle1"
                  fontWeight="fontWeightBold"
                  mt="32px !important"
                >
                  Diagnosis
                </Typography>

                <Grid item container xs={12} justifyContent="space-between">
                  <Grid item xs={4}></Grid>
                  <Grid
                    item
                    xs={4}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column"
                    }}
                  >
                    <Button
                      sx={{ display: "flex", flexDirection: "column" }}
                      onClick={copyFromLeftToRight}
                    >
                      Copy From Left to Right
                      <ArrowRightAltIcon />
                    </Button>
                  </Grid>
                  <Grid item xs={4}></Grid>
                  <Grid container item xs={5.5} gap={2}>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="fontWeightBold"
                        align="center"
                        color={blue[900]}
                      >
                        Left Ear
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <SearchableSelectFormField
                        options={HEARING_LOSS_LEVEL.getOptions()}
                        name="diagnosis.hearing_loss_left"
                        label="Hearing Loss Level (Severity)"
                        control={control}
                        disabled={diagnosisIsDisabled}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <SelectFormField
                        options={HEARING_LOSS_TYPE.getOptions()}
                        name="diagnosis.hearing_loss_type_left"
                        label="Hearing Loss Type"
                        control={control}
                        disabled={diagnosisIsDisabled}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <SelectFormField
                        options={HEARING_LOSS_SHAPE.getOptions()}
                        name="diagnosis.hearing_loss_shape_left"
                        label="Hearing Loss Shape"
                        control={control}
                        disabled={diagnosisIsDisabled}
                      />
                    </Grid>
                  </Grid>
                  <Grid container item xs={5.5} gap={2}>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="fontWeightBold"
                        align="center"
                        color={red[900]}
                      >
                        Right Ear
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <SearchableSelectFormField
                        options={HEARING_LOSS_LEVEL.getOptions()}
                        name="diagnosis.hearing_loss_right"
                        label="Hearing Loss Level (Severity)"
                        control={control}
                        disabled={diagnosisIsDisabled}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <SelectFormField
                        options={HEARING_LOSS_TYPE.getOptions()}
                        name="diagnosis.hearing_loss_type_right"
                        label="Hearing Loss Type"
                        control={control}
                        disabled={diagnosisIsDisabled}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <SelectFormField
                        options={HEARING_LOSS_SHAPE.getOptions()}
                        name="diagnosis.hearing_loss_shape_right"
                        label="Hearing Loss Shape"
                        control={control}
                        disabled={diagnosisIsDisabled}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}

            <Typography
              variant="subtitle1"
              fontWeight="fontWeightBold"
              mt="32px !important"
            >
              Appointment Outcome
            </Typography>
            {!isCancellation && newAppointmentOutcomeChangesEnabled ? (
              <Grid container>
                <Grid item xs={12} mt={2}>
                  <ICDCodesFormField
                    name="diagnosis.icd10_codes"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if ((value as ICD10Code[]).length > 12) {
                          return `Please select up to twelve ICD-10 codes`;
                        }

                        return true;
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} mt={2}>
                  <WordProcessorField
                    name="outcome_notes"
                    control={control}
                    required
                    placeholder="Please enter the Chart Note *"
                    isLoading={isLoading}
                    templateTypes={DEFAULT_TEMPLATE_TYPES}
                  />
                </Grid>
              </Grid>
            ) : (
              <>
                {!isCancellation && (
                  <>
                    <SelectFormField
                      name="outcome"
                      label="Outcome"
                      control={control}
                      options={outcomeOptions}
                      required
                      onChange={() => {
                        setValue("outcome_reason", "", {
                          shouldValidate: true
                        });
                      }}
                    />

                    {Boolean(reasons.length) && (
                      <SelectFormField
                        control={control}
                        name="outcome_reason"
                        label="Reason"
                        options={reasons.map(
                          (reason: AppointmentOutcomeReason) => ({
                            value: reason.id,
                            label: reason.name
                          })
                        )}
                        shouldUnregister
                        required
                      />
                    )}
                  </>
                )}
                <InputFormField
                  control={control}
                  name="outcome_notes"
                  label="Outcome notes"
                  placeholder=""
                  multiline
                />
              </>
            )}

            {!isCancellation && (
              <>
                <Typography
                  variant="subtitle1"
                  fontWeight="fontWeightBold"
                  mt="32px !important"
                >
                  Assigned Patient Tags
                </Typography>

                {patientId && <PatientAssignedTags patientId={patientId} />}
              </>
            )}

            {!isCancellation && appointment && patientId && (
              <>
                <Typography
                  variant="subtitle1"
                  fontWeight="fontWeightBold"
                  mt="32px !important"
                >
                  Appointment Checklist
                </Typography>
                {(!newAppointmentOutcomeChangesEnabled ||
                  configurableChecklistEnabled) && (
                  <AppointmentChecklist
                    appointmentId={appointment.id}
                    patientId={patientId}
                    hasGeneratedInvoice={Boolean(
                      appointment.appointmentType.generate_sale
                    )}
                    setCanAppointmentBeCompleted={setCanAppointmentBeCompleted}
                    sx={{ pt: 0, pr: 1 }}
                  />
                )}
                {(companionPresentFieldVisible ||
                  newAppointmentOutcomeChangesEnabled ||
                  hearingAidDemoFieldVisible) && (
                  <Grid container>
                    {newAppointmentOutcomeChangesEnabled && (
                      <Grid item xs={6}>
                        <InputFormField
                          name="telehealth_provider"
                          label="Telehealth Provider"
                          control={control}
                          sx={{ m: 0 }}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="fontWeightBold"
                        mt="32px !important"
                      >
                        Appointment Companion
                      </Typography>
                      <FormControlLabel
                        label="Was a companion present?"
                        control={
                          <CheckboxFormField
                            name="companion_present"
                            control={control}
                            testId="companion_present"
                            sx={{ ml: 0 }}
                          />
                        }
                        sx={{ mt: 1 }}
                      />
                    </Grid>

                    {isCompanionPresent && (
                      <Grid item container xs={6}>
                        <Grid item xs={6}>
                          <SelectFormField
                            name="companion_type"
                            label="Companion type"
                            options={CONTACT_RELATIONSHIP.getOptions()}
                            control={control}
                            required
                            sx={{ mt: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <InputFormField
                            name="companion_name"
                            label="Companion Name"
                            control={control}
                            required
                          />
                        </Grid>
                      </Grid>
                    )}

                    {hearingAidDemoFieldVisible && (
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle1"
                          fontWeight="fontWeightBold"
                          mt="32px !important"
                        >
                          Hearing Aid Demonstration
                        </Typography>
                        <FormControlLabel
                          label="Was a hearing aid demonstration performed?"
                          control={
                            <CheckboxFormField
                              name="hearing_aid_demo_performed"
                              control={control}
                              testId="hearing_aid_demo_performed"
                              sx={{ ml: 0 }}
                            />
                          }
                          sx={{ mt: 1 }}
                        />
                      </Grid>
                    )}
                  </Grid>
                )}
              </>
            )}
          </Stack>

          <DialogActions>
            <LoadingButton
              data-testid="confirm-dialog-confirm"
              variant="contained"
              disabled={!isValid}
              loading={isUpdatingOutcome}
              sx={{ minWidth: 320 }}
              onClick={handleSubmit}
            >
              Confirm
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </form>
    </FormProvider>
  );
}
